// apps/web/src/features/russia-requests/useRussiaRequestsFilters.ts
//
// Issue #942. URL-canonical filter state for `/russia-rates`. Owns four
// keys — `status`, `type`, `managerId`, `q` — backed by
// `useSearchParams`. Drawer-owned keys (`tab` from
// `useRussiaRequestUrlState`) survive every filter mutation.
//
// Thinner than china's `useRequestsFilters`: russia-rates has no SLA,
// no unread, no advanced panel, no localStorage layer in this slice —
// the URL is the only persistent surface.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  RequestStatus,
  RussiaRequestType,
  isStatusHiddenFromLogist,
  type RussiaRequestListQuery,
} from '@magimp/shared';

import { useAuthStore } from '../../stores/auth.js';
import {
  detectStatusFilterRole,
  isLogistStatusFilterRole,
  type StatusFilterRole,
} from '../../shared/index.js';
import { useRussiaRequestManagerOptions } from './queries.js';

/**
 * @aiPurpose hard wire-limit for the `q` substring search. Mirrors the
 *   `q.max(200)` rule in `RussiaRequestListQuerySchema`; both the
 *   `<Input maxLength=...>` and the wire-build path clamp to it so a
 *   long paste cannot blow past the schema.
 */
export const RUSSIA_SEARCH_MAX_CHARS = 200;

const STATUS_VALUES = new Set<string>(Object.values(RequestStatus));
const TYPE_VALUES = new Set<string>(Object.values(RussiaRequestType));
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const RUSSIA_FILTER_PARAM_KEYS = ['status', 'type', 'managerId', 'q'] as const;

/**
 * @aiPurpose URL-canonical filter shape consumed by the list page.
 *   All four fields are optional — absence means "no filter".
 */
export interface RussiaRequestsFiltersState {
  status: RequestStatus | null;
  type: RussiaRequestType | null;
  managerId: string;
  q: string;
}

function readStatus(sp: URLSearchParams, role: StatusFilterRole): RequestStatus | null {
  const raw = sp.get('status');
  if (raw === null) return null;
  if (!STATUS_VALUES.has(raw)) return null;
  const status = raw as RequestStatus;
  // INVARIANT (#1159 PR5): a logist actor's shared link with
  //   `?status=DRAFT` or `?status=RETURN` silently collapses to "no
  //   chip selected". The writer effect re-canonicalises the URL via
  //   `history.replaceState` on the same effect pass, so the malformed
  //   param disappears without flashing the row.
  if (isLogistStatusFilterRole(role) && isStatusHiddenFromLogist(status)) return null;
  return status;
}

function readType(sp: URLSearchParams): RussiaRequestType | null {
  const raw = sp.get('type');
  if (raw === null) return null;
  return TYPE_VALUES.has(raw) ? (raw as RussiaRequestType) : null;
}

function readManagerId(sp: URLSearchParams): string {
  const raw = sp.get('managerId');
  if (raw === null) return '';
  return UUID_RE.test(raw) ? raw : '';
}

function readQ(sp: URLSearchParams): string {
  const raw = sp.get('q');
  if (raw === null) return '';
  // WHY: the wire schema trims + caps at 200 chars; mirror those rules
  //   on the read path so a hostile shared URL with leading/trailing
  //   whitespace or an oversize payload does not paint a value the
  //   input cannot reproduce on its own.
  return raw.slice(0, RUSSIA_SEARCH_MAX_CHARS);
}

function readFromSearchParams(
  sp: URLSearchParams,
  role: StatusFilterRole,
): RussiaRequestsFiltersState {
  return {
    status: readStatus(sp, role),
    type: readType(sp),
    managerId: readManagerId(sp),
    q: readQ(sp),
  };
}

/**
 * @aiPurpose write canonical filter params to the next `URLSearchParams`
 *   without disturbing drawer-owned keys. Empty / null values produce no
 *   param (AC: "Empty filters do not appear in the URL"). The drawer's
 *   `?tab=` and any other unknown keys are passed through unchanged.
 */
function writeFiltersToSearchParams(
  prev: URLSearchParams,
  next: RussiaRequestsFiltersState,
): URLSearchParams {
  const out = new URLSearchParams(prev);
  for (const k of RUSSIA_FILTER_PARAM_KEYS) {
    out.delete(k);
  }
  if (next.status) out.set('status', next.status);
  if (next.type) out.set('type', next.type);
  if (next.managerId) out.set('managerId', next.managerId);
  const trimmedQ = next.q.trim().slice(0, RUSSIA_SEARCH_MAX_CHARS);
  if (trimmedQ.length > 0) out.set('q', trimmedQ);
  return out;
}

function serializeFilterSurface(sp: URLSearchParams): string {
  const parts: string[] = [];
  for (const k of RUSSIA_FILTER_PARAM_KEYS) {
    const v = sp.get(k);
    if (v != null && v.length > 0) parts.push(`${k}=${v}`);
  }
  return parts.join('&');
}

export interface UseRussiaRequestsFiltersInput {
  /** Caller may pick a manager filter (russiaRequests.scope.all). */
  canPickManager: boolean;
}

export interface UseRussiaRequestsFiltersResult {
  status: RequestStatus | null;
  type: RussiaRequestType | null;
  managerId: string;
  q: string;
  setStatus: (next: RequestStatus | null) => void;
  setType: (next: RussiaRequestType | null) => void;
  setManagerId: (next: string) => void;
  setQ: (next: string) => void;
  /**
   * Wire-filter shape ready for `useRussiaRequestsList`. `null` while a
   * hydrated `managerId` is still being validated against
   * `/russia-requests/manager-options`. Callers MUST treat `null` as
   * "loading" and skip the list query — otherwise a brief no-manager
   * window would flash behind a stale filter chip.
   */
  wireFilters: Partial<RussiaRequestListQuery> | null;
  /** True while manager-options validation is suppressing wireFilters. */
  isValidatingManager: boolean;
}

/**
 * @aiPurpose URL-canonical filter ownership for `/russia-rates` (issue
 *   #942). Reads on mount, canonicalises the URL with a replace
 *   navigation, exposes typed setters, and gates the wire query until
 *   any hydrated `managerId` has been validated.
 * @aiInvariant the URL writer always preserves drawer-owned keys
 *   (`tab`). External writers (`useRussiaRequestUrlState`) own those
 *   keys; this hook is the sole writer of `status`, `type`, `managerId`,
 *   `q`.
 */
export function useRussiaRequestsFilters(
  input: UseRussiaRequestsFiltersInput,
): UseRussiaRequestsFiltersResult {
  const { canPickManager } = input;
  const [searchParams, setSearchParams] = useSearchParams();
  // INVARIANT (#1159 PR5): role is pinned at first render so URL
  //   canonicalisation + back/forward URL→state syncback share the same
  //   role-aware status reader. A mid-session role swap is a logout-
  //   bounded event today, so a stable read on mount is sufficient.
  const role = detectStatusFilterRole(useAuthStore.getState().user);

  // INVARIANT: hydrate from URL exactly once. Subsequent URL changes
  //   (back/forward) flow through the search-params subscription and
  //   the dedicated effect below — not through this initial read.
  const initialRef = useRef<RussiaRequestsFiltersState | null>(null);
  if (initialRef.current === null) {
    const hydrated = readFromSearchParams(searchParams, role);
    initialRef.current = {
      ...hydrated,
      managerId: canPickManager ? hydrated.managerId : '',
    };
  }

  const [state, setState] = useState<RussiaRequestsFiltersState>(initialRef.current);

  // Manager-id validation. Needed only when the URL came in with a
  // managerId AND the caller can pick one.
  const needsManagerValidation = useMemo(
    () => canPickManager && initialRef.current!.managerId.length > 0,
    [canPickManager],
  );
  const [managerValidating, setManagerValidating] = useState<boolean>(needsManagerValidation);

  const managerOptionsQuery = useRussiaRequestManagerOptions({
    enabled: managerValidating,
  });

  useEffect(() => {
    if (!managerValidating) return;
    if (!canPickManager) {
      // Permission lost between mount and effect — drop the id.
      setState((p) => (p.managerId ? { ...p, managerId: '' } : p));
      setManagerValidating(false);
      return;
    }
    if (managerOptionsQuery.isSuccess) {
      const visible = new Set(managerOptionsQuery.data.map((m) => m.id));
      setState((p) => (p.managerId && !visible.has(p.managerId) ? { ...p, managerId: '' } : p));
      setManagerValidating(false);
      return;
    }
    if (managerOptionsQuery.isError) {
      // After retries the query gave up. Drop the id and continue.
      setState((p) => (p.managerId ? { ...p, managerId: '' } : p));
      setManagerValidating(false);
    }
  }, [
    managerValidating,
    canPickManager,
    managerOptionsQuery.isSuccess,
    managerOptionsQuery.isError,
    managerOptionsQuery.data,
  ]);

  // INVARIANT: split into a state→URL writer and a URL→state syncback.
  //   `justWroteRef` is set TRUE when the writer dispatched a
  //   `setSearchParams` and the syncback consumes it on its very next
  //   run, so the stale URL we just replaced cannot be pulled back into
  //   state during the write's settle window.
  const justWroteRef = useRef(false);

  // INVARIANT (#942 / Codex F-001): chip + manager-picker changes push
  //   a new history entry so browser back/forward navigates between
  //   prior filter selections. Search input typing and effect-driven
  //   canonical cleanup (invalid-enum drop on mount, manager-id drop
  //   after validation) use replace so the user does not have to press
  //   Back N times to undo a single search session, and a deep-linked
  //   stale URL does not bury the prior route in history. The next
  //   queued write mode is read off this ref and reset to `replace`
  //   after consumption.
  const nextWriteModeRef = useRef<'push' | 'replace'>('replace');

  // Pin setSearchParams in a ref so an external URL change does not
  // re-trigger the write effect.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  useEffect(() => {
    if (managerValidating) {
      // Hold off URL writes while the hydrated id is being validated:
      // a fast invalid drop would otherwise reach the URL after a
      // refresh and re-hydrate the same stale id.
      return;
    }
    const sp = searchParamsRef.current;
    const next = writeFiltersToSearchParams(sp, state);
    const nextSurface = serializeFilterSurface(next);
    const prevSurface = serializeFilterSurface(sp);
    if (nextSurface === prevSurface) return;
    justWroteRef.current = true;
    const replace = nextWriteModeRef.current === 'replace';
    nextWriteModeRef.current = 'replace';
    setSearchParamsRef.current(next, { replace });
  }, [state, managerValidating]);

  // Sync local state when the URL changes from outside the hook —
  // back/forward navigation, deep-link replay, or a future writer.
  // INVARIANT: skip the next URL settle that came from our own write,
  //   detected via `justWroteRef`.
  useEffect(() => {
    if (managerValidating) return;
    if (justWroteRef.current) {
      justWroteRef.current = false;
      return;
    }
    const fromUrl = readFromSearchParams(searchParams, role);
    const nextManagerId = canPickManager ? fromUrl.managerId : '';
    setState((prev) => {
      if (
        prev.status === fromUrl.status &&
        prev.type === fromUrl.type &&
        prev.managerId === nextManagerId &&
        prev.q === fromUrl.q
      ) {
        return prev;
      }
      return {
        status: fromUrl.status,
        type: fromUrl.type,
        managerId: nextManagerId,
        q: fromUrl.q,
      };
    });
  }, [searchParams, managerValidating, canPickManager, role]);

  const setStatus = useCallback((next: RequestStatus | null) => {
    setState((p) => {
      if (p.status === next) return p;
      nextWriteModeRef.current = 'push';
      return { ...p, status: next };
    });
  }, []);

  const setType = useCallback((next: RussiaRequestType | null) => {
    setState((p) => {
      if (p.type === next) return p;
      nextWriteModeRef.current = 'push';
      return { ...p, type: next };
    });
  }, []);

  const setManagerId = useCallback((next: string) => {
    setState((p) => {
      if (p.managerId === next) return p;
      nextWriteModeRef.current = 'push';
      return { ...p, managerId: next };
    });
  }, []);

  const setQ = useCallback((next: string) => {
    // INVARIANT: cap on write so any path into state respects the wire
    //   bound, even if a caller bypasses the native maxLength. Search
    //   typing uses replace (default) so back/forward does not have to
    //   walk per-keystroke entries.
    const clamped = next.slice(0, RUSSIA_SEARCH_MAX_CHARS);
    setState((p) => (p.q === clamped ? p : { ...p, q: clamped }));
  }, []);

  const wireFilters = useMemo<Partial<RussiaRequestListQuery> | null>(() => {
    if (managerValidating) return null;
    const out: Partial<RussiaRequestListQuery> = {};
    if (state.status) out.status = state.status;
    if (state.type) out.type = state.type;
    if (state.managerId) out.managerId = state.managerId;
    const trimmedQ = state.q.trim().slice(0, RUSSIA_SEARCH_MAX_CHARS);
    if (trimmedQ.length > 0) out.q = trimmedQ;
    return out;
  }, [state, managerValidating]);

  return {
    status: state.status,
    type: state.type,
    managerId: state.managerId,
    q: state.q,
    setStatus,
    setType,
    setManagerId,
    setQ,
    wireFilters,
    isValidatingManager: managerValidating,
  };
}
